import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { SettingSectionProps, TacticMapSettings } from "@/app/report/common/type"
import { colors } from "@/components/colors.ts"
import TextField from "@/components/form/textField/TextField.tsx"
import ColorPicker from "@/app/report/components/setting/ColorPicker.tsx"
import Checkbox from "@/components/form/checkbox/Checkbox.tsx"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook.ts"
import Selector from "@/components/form/selector/Selector.tsx"
import { SECTION_TYPE } from "@/app/report/common/constants.ts"
import { DEFAULT_ACTION_TYPES, DEFAULT_MAP_TYPES } from "@/app/report/common/tacticMapType"

const DEFAULT_FIXED_COLUMNS = ["number", "uniformNumber", "occurrence"]

const DEFAULT_TACTIC_MAP_SETTING_ITEM = {
  title: "Action Map",
  color: colors.red[700],
  defaultMapType: "formationMap",
  fixedColumns: DEFAULT_FIXED_COLUMNS,
  defaultActionType: "R2",
}

export default function TacticMapSection({
  settings,
  tempTemplate,
  setTempTemplate,
  sectionIndex,
}: SettingSectionProps<TacticMapSettings> & { sectionIndex: number }) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()
  const [tempTacticItem, setTempTacticItem] = useState<TacticMapSettings>(
    DEFAULT_TACTIC_MAP_SETTING_ITEM,
  )

  useEffect(() => {
    setTempTacticItem(settings)
  }, [settings])

  useEffect(() => {
    setTempTemplate((prev) => {
      if (!prev) return null
      const updatedContent = [...prev.content]
      updatedContent[sectionIndex] = { ...updatedContent[sectionIndex], settings: tempTacticItem }
      return { ...prev, content: updatedContent }
    })
  }, [tempTacticItem, sectionIndex, setTempTemplate])

  return (
    <div className="DailySettingTacticMapSectionContainer">
      <div className="daily-setting-tactic-map-title-header">
        <TextField
          label={t("title")}
          value={tempTacticItem.title}
          onChange={(e) => setTempTacticItem({ ...tempTacticItem, title: e.target.value })}
          error={tempTacticItem.title.length > 35 || tempTacticItem.title.length < 1}
          showMessage={tempTacticItem.title.length > 35 || tempTacticItem.title.length < 1}
          message={
            tempTacticItem.title.length > 35
              ? t("rules.max_len", { name: t("title"), max_len: 35 })
              : t("rules.required")
          }
        />
        <ColorPicker
          color={tempTacticItem.color}
          setColor={(color) =>
            setTempTacticItem((prev) => {
              return { ...prev, color: color }
            })
          }
        />
      </div>
      <div className="daily-setting-tactic-map-default">
        <div className="daily-setting-tactic-map-default-map-type">
          <p>{t("defaultMapType")}</p>
          <Selector
            options={DEFAULT_MAP_TYPES}
            value={{
              value: tempTacticItem.defaultMapType,
              label: t(
                DEFAULT_MAP_TYPES.find((item) => item.value === tempTacticItem.defaultMapType)
                  ?.label || "",
              ),
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props

              if (!option) {
                return null
              }

              return (
                <li key={key} {...optionProps}>
                  <p>{t(option.label)}</p>
                </li>
              )
            }}
            onChange={(_: unknown, { value }: { value: string; label: string }) => {
              setTempTacticItem((prev) => {
                return {
                  ...prev,
                  defaultMapType: value,
                }
              })
            }}
            placeholder={t("select")}
          />
        </div>
        <div className="daily-setting-tactic-default-action-type">
          <p>{t("defaultActionType")}</p>
          <Selector
            options={Object.entries(DEFAULT_ACTION_TYPES).map(([, item]) => ({
              value: item.value,
              label: translateKey(item.label),
            }))}
            value={{
              value: tempTacticItem.defaultActionType,
              label: translateKey(
                DEFAULT_ACTION_TYPES.find((item) => item.value === tempTacticItem.defaultActionType)
                  ?.label || "",
              ),
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props

              if (!option) {
                return null
              }

              return (
                <li key={key} {...optionProps}>
                  <p>{option.label}</p>
                </li>
              )
            }}
            onChange={(_: unknown, { value }: { value: string; label: string }) => {
              setTempTacticItem((prev) => {
                return {
                  ...prev,
                  defaultActionType: value,
                }
              })
            }}
            placeholder={t("select")}
          />
        </div>
      </div>
      <div className="daily-setting-tactic-map-fixed-column">
        <p>{t("fixedColumn")}</p>
        <div>
          {DEFAULT_FIXED_COLUMNS.map((item) => (
            <Checkbox
              key={item}
              label={t(item)}
              value={item}
              onChange={(_, checked) => {
                if (checked) {
                  setTempTacticItem((prev) => {
                    const updated = { ...prev }
                    updated.fixedColumns = [...updated.fixedColumns, item]

                    return updated
                  })
                } else {
                  setTempTacticItem((prev) => {
                    const updated = { ...prev }
                    updated.fixedColumns = updated.fixedColumns.filter((i) => i !== item)

                    return updated
                  })
                }
              }}
              checked={tempTacticItem.fixedColumns.includes(item)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
